import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../context/AppContext';
import { usePhone } from '../../hooks/usePhone';
import { useI18n } from '../../i18n/useI18n';
import { useDismiss } from '../../hooks/useDismiss';
import { createId } from '../../utils/id';
import { focusSelector } from '../../utils/focus';
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconClose,
  IconDotsVertical,
  IconList,
  IconMenu,
  IconMinus,
  IconPencil,
} from '../icons/Icons';
import { TaskColumn } from './TaskColumn';
import board from './board.module.css';
import styles from './lists.module.css';

function openCount(item) {
  return item.lists.reduce(
    (sum, list) => sum + list.tasks.filter((task) => !task.done).length,
    0
  );
}

export function ListPanel() {
  const { state, actions } = useApp();
  const { t, dir } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tabMenu, setTabMenu] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [managingId, setManagingId] = useState(null);
  const plusRef = useRef(null);
  const menuRef = useRef(null);
  const tabMenuRef = useRef(null);
  const tabPopRef = useRef(null);
  const columnsRef = useRef(null);
  const [edges, setEdges] = useState({ prev: false, next: false });
  useDismiss(menuOpen, () => setMenuOpen(false), [plusRef, menuRef]);
  useDismiss(Boolean(tabMenu), () => setTabMenu(null), [tabMenuRef, tabPopRef]);

  const page = state.pages.find((item) => item.id === state.activePageId) || state.pages[0];
  const managed = state.pages.find((item) => item.id === managingId) || null;
  const expanded = state.listsExpanded;
  const phone = usePhone();
  const dayCols = phone ? 1 : state.preferences.columns;
  const showLines = state.preferences.showLines;

  const measureLists = () => {
    const el = columnsRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const pos = el.scrollLeft < 0 ? -el.scrollLeft : el.scrollLeft;
    setEdges({ prev: pos > 4, next: max - pos > 4 });
  };

  useLayoutEffect(() => {
    measureLists();
  }, [page?.id, page?.lists.length, dayCols, expanded]);

  useEffect(() => {
    const el = columnsRef.current;
    if (!el) return undefined;
    const onScroll = () => measureLists();
    el.addEventListener('scroll', onScroll, { passive: true });
    const observer = new ResizeObserver(() => measureLists());
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [expanded, page?.id]);

  const scrollLists = (count) => {
    const el = columnsRef.current;
    if (!el) return;
    const step = el.clientWidth / dayCols;
    const sign = dir === 'rtl' ? -1 : 1;
    el.scrollBy({ left: sign * count * step, behavior: 'smooth' });
  };

  const onAddList = (pageId) => {
    const id = createId();
    actions.addList(pageId, id);
    actions.setListsExpanded(true);
    if (managingId !== pageId) {
      requestAnimationFrame(() => {
        const el = columnsRef.current;
        if (el) {
          const end = dir === 'rtl' ? -el.scrollWidth : el.scrollWidth;
          el.scrollTo({ left: end, behavior: 'smooth' });
        }
        focusSelector(`[data-focus="title-${id}"]`, { select: true });
      });
    }
    return id;
  };

  const onAddPage = () => {
    const id = createId();
    actions.addPage(id);
    actions.setListsExpanded(true);
    setEditing(id);
    setManagingId(null);
  };

  const openTabMenu = (item, button) => {
    actions.selectPage(item.id);
    if (tabMenu === item.id) {
      setTabMenu(null);
      return;
    }
    const rect = button.getBoundingClientRect();
    setMenuAnchor({
      top: rect.top,
      left: rect.left,
      right: window.innerWidth - rect.right,
    });
    setTabMenu(item.id);
    setMenuOpen(false);
  };

  return (
    <section
      className={styles.panel}
      style={{ height: expanded ? (phone ? Math.min(state.listsHeight, 280) : state.listsHeight) : 44 }}
      aria-label={t('lists.label')}
    >
      <div className={styles.tabs}>
        <div className={styles.tabList} role="tablist" aria-label={t('lists.pages')}>
          {state.pages.map((item) => (
            <div
              key={item.id}
              className={styles.tabItem}
              data-selected={item.id === page?.id ? 'true' : 'false'}
              ref={tabMenu === item.id ? tabMenuRef : null}
              onContextMenu={(event) => {
                if (event.target.closest('input, [role="menu"]')) return;
                event.preventDefault();
                if (state.pages.length <= 1) return;
                setTabMenu(null);
                if (managingId === item.id) setManagingId(null);
                actions.deletePage(item.id);
              }}
            >
              <button
                type="button"
                className={styles.dots}
                aria-label={t('lists.manageTab')}
                aria-haspopup="menu"
                aria-expanded={tabMenu === item.id}
                onClick={(event) => openTabMenu(item, event.currentTarget)}
              >
                <IconDotsVertical />
              </button>
              {editing === item.id ? (
                <input
                  className={styles.tabInput}
                  dir="auto"
                  autoFocus
                  defaultValue={item.name}
                  aria-label={t('lists.pageName')}
                  onFocus={(event) => event.target.select()}
                  onBlur={(event) => {
                    actions.renamePage(item.id, event.target.value);
                    setEditing(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                  }}
                />
              ) : (
                <button
                  type="button"
                  role="tab"
                  className={styles.tab}
                  dir="auto"
                  aria-selected={item.id === page?.id}
                  title={state.pages.length > 1 ? t('lists.pageHint') : t('lists.rename')}
                  onClick={() => {
                    actions.selectPage(item.id);
                    if (managingId && managingId !== item.id) setManagingId(null);
                  }}
                  onDoubleClick={() => setEditing(item.id)}
                >
                  <span className={styles.tabName}>{item.name}</span>
                  <span className={styles.tabCount}>{openCount(item)}</span>
                </button>
              )}
              {tabMenu === item.id &&
                menuAnchor &&
                createPortal(
                  <div
                    ref={tabPopRef}
                    className={styles.tabMenu}
                    role="menu"
                    aria-label={t('lists.manageTab')}
                    style={
                      dir === 'rtl'
                        ? { top: menuAnchor.top, right: menuAnchor.right }
                        : { top: menuAnchor.top, left: menuAnchor.left }
                    }
                  >
                    <p className={styles.menuKicker}>{t('lists.manageTab')}</p>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setTabMenu(null);
                        actions.selectPage(item.id);
                        actions.setListsExpanded(true);
                        setManagingId(item.id);
                      }}
                    >
                      <IconList />
                      {t('lists.manageLists')}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setTabMenu(null);
                        setEditing(item.id);
                      }}
                    >
                      <IconPencil />
                      {t('lists.renameTab')}
                    </button>
                  </div>,
                  document.body
                )}
            </div>
          ))}
        </div>
        <div className={styles.plusWrap}>
          <button
            ref={plusRef}
            type="button"
            className={styles.plus}
            aria-label={t('lists.add')}
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((open) => !open);
              setTabMenu(null);
            }}
          >
            +
          </button>
          {menuOpen && (
            <div ref={menuRef} className={styles.menu} role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  if (page) onAddList(page.id);
                }}
              >
                {t('lists.newList')}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  onAddPage();
                }}
              >
                {t('lists.newPage')}
              </button>
              {state.pages.length > 1 && page && (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.danger}
                  onClick={() => {
                    setMenuOpen(false);
                    if (managingId === page.id) setManagingId(null);
                    actions.deletePage(page.id);
                  }}
                >
                  {t('lists.deletePage')}
                </button>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.chevron}
          data-open={expanded ? 'true' : 'false'}
          aria-expanded={expanded}
          aria-label={expanded ? t('lists.collapse') : t('lists.expand')}
          onClick={() => actions.setListsExpanded(!expanded)}
        >
          <IconChevronDown />
        </button>
      </div>

      {managed && (
        <ManageLists
          page={managed}
          onClose={() => setManagingId(null)}
          onAddList={() => onAddList(managed.id)}
        />
      )}

      {expanded && page && (
        <div className={`${board.daysWrap} ${styles.listFrame}`}>
        <div
          ref={columnsRef}
          className={styles.columns}
          style={{ '--day-cols': dayCols }}
          data-lines={showLines ? 'true' : 'false'}
        >
          {page.lists.map((list) => (
            <section
              key={list.id}
              className={`${board.column} ${styles.listCol}`}
              data-zone="list"
              data-page-id={page.id}
              data-list-id={list.id}
              data-length={list.tasks.length}
              aria-label={list.title || t('lists.list')}
            >
              <div className={styles.listHead}>
                <input
                  className={styles.listTitle}
                  data-focus={`title-${list.id}`}
                  value={list.title}
                  aria-label={t('lists.listName', { name: list.title || t('lists.untitled') })}
                  onChange={(event) => actions.renameList(page.id, list.id, event.target.value)}
                  onBlur={(event) => {
                    if (!event.target.value.trim()) {
                      actions.renameList(page.id, list.id, t('lists.untitled'));
                    }
                  }}
                />
                <button
                  type="button"
                  className={styles.deleteList}
                  aria-label={t('lists.delete', { name: list.title || t('lists.list') })}
                  onClick={() => actions.deleteList(page.id, list.id)}
                >
                  <IconClose />
                </button>
              </div>
              <TaskColumn
                tasks={list.tasks}
                scope={`list-${list.id}`}
                loc={{ zone: 'list', pageId: page.id, listId: list.id }}
                placeholder={t('task.add')}
              />
            </section>
          ))}
          <section className={`${board.column} ${styles.listCol} ${styles.newCol}`} aria-label={t('lists.newList')}>
            <div className={styles.newListHead}>
              <button type="button" className={styles.newList} onClick={() => onAddList(page.id)}>
                + {t('lists.newList')}
              </button>
            </div>
            <div className={board.scroller} data-lines={showLines ? 'true' : 'false'} />
          </section>
        </div>
        {edges.prev && (
          <div className={`${board.dayNav} ${board.dayNavLeft} ${styles.listNav}`}>
            <button
              type="button"
              className={board.dayNavBtn}
              aria-label={t('lists.prev')}
              title={t('lists.prev')}
              data-mirror=""
              onClick={() => scrollLists(-1)}
            >
              <IconChevronLeft />
            </button>
            {dayCols > 1 && (
              <button
                type="button"
                className={board.dayNavBtn}
                aria-label={t('lists.prevMany', { n: dayCols })}
                title={t('lists.prevMany', { n: dayCols })}
                data-mirror=""
                onClick={() => scrollLists(-dayCols)}
              >
                <IconChevronsLeft />
              </button>
            )}
          </div>
        )}
        {edges.next && (
          <div className={`${board.dayNav} ${board.dayNavRight} ${styles.listNav}`}>
            <button
              type="button"
              className={board.dayNavBtn}
              aria-label={t('lists.next')}
              title={t('lists.next')}
              data-mirror=""
              onClick={() => scrollLists(1)}
            >
              <IconChevronRight />
            </button>
            {dayCols > 1 && (
              <button
                type="button"
                className={board.dayNavBtn}
                aria-label={t('lists.nextMany', { n: dayCols })}
                title={t('lists.nextMany', { n: dayCols })}
                data-mirror=""
                onClick={() => scrollLists(dayCols)}
              >
                <IconChevronsRight />
              </button>
            )}
          </div>
        )}
        </div>
      )}
    </section>
  );
}

function ManageLists({ page, onClose, onAddList }) {
  const { actions } = useApp();
  const { t } = useI18n();
  const [editingId, setEditingId] = useState(null);
  const [drag, setDrag] = useState(null);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onPointerDown = (event, index) => {
    if (event.button !== 0) return;
    if (event.target.closest('[data-no-drag]')) return;
    const listEl = event.currentTarget.parentElement;
    const startY = event.clientY;
    const slots = [...listEl.querySelectorAll('[data-list-index]')].map((row) => {
      const rect = row.getBoundingClientRect();
      return { index: Number(row.dataset.listIndex), top: rect.top, height: rect.height };
    });
    const height = slots[index]?.height || 44;
    const minDy = -index * height;
    const maxDy = (slots.length - 1 - index) * height;
    let dragging = false;
    let over = index;

    const pointIndex = (y) => {
      for (const slot of slots) {
        if (y < slot.top + slot.height / 2) return slot.index;
      }
      return slots.length - 1;
    };

    const onMove = (moveEvent) => {
      if (!dragging) {
        if (Math.abs(moveEvent.clientY - startY) < 4) return;
        dragging = true;
        document.body.classList.add('is-dragging');
      }
      const dy = Math.max(minDy, Math.min(maxDy, moveEvent.clientY - startY));
      over = pointIndex(startY + dy);
      setDrag({ index, dy, over, height });
    };

    const finish = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      document.body.classList.remove('is-dragging');
      if (dragging && over !== index) actions.reorderLists(page.id, index, over);
      setDrag(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  };

  const rowShift = (index) => {
    if (!drag || drag.index === index) return 0;
    if (drag.index < drag.over && index > drag.index && index <= drag.over) return -1;
    if (drag.index > drag.over && index >= drag.over && index < drag.index) return 1;
    return 0;
  };

  return createPortal(
    <div className={styles.scrim} onMouseDown={onClose}>
      <div
        className={styles.manager}
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-lists-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
      <div className={styles.managerHead}>
        <h2 id="manage-lists-title" className={styles.managerTitle} dir="auto">
          {page.name}
        </h2>
        <button type="button" className={styles.managerClose} aria-label={t('lists.close')} onClick={onClose}>
          <IconClose />
        </button>
      </div>
      <div className={styles.managerList} role="list">
        {page.lists.length === 0 && (
          <button type="button" className={styles.managerEmpty} onClick={onAddList}>
            {t('lists.create')}
          </button>
        )}
        {page.lists.map((list, index) => {
          const shift = rowShift(index);
          const lifted = drag?.index === index;
          const transform = lifted
            ? `translate3d(0, ${drag.dy}px, 0)`
            : shift
              ? `translate3d(0, ${shift * drag.height}px, 0)`
              : undefined;
          return (
          <div
            key={list.id}
            className={styles.manageRow}
            role="listitem"
            data-list-index={index}
            data-dragging={lifted ? 'true' : 'false'}
            data-shifting={shift !== 0 ? 'true' : 'false'}
            style={transform ? { transform } : undefined}
            onPointerDown={(event) => onPointerDown(event, index)}
          >
            <span className={styles.manageGrip} title={t('lists.dragList')}>
              <IconMenu />
            </span>
            {editingId === list.id ? (
              <input
                className={styles.manageInput}
                dir="auto"
                autoFocus
                data-no-drag="true"
                defaultValue={list.title}
                aria-label={t('lists.renameList')}
                onFocus={(event) => event.target.select()}
                onBlur={(event) => {
                  const next = event.target.value.trim() || t('lists.untitled');
                  actions.renameList(page.id, list.id, next);
                  setEditingId(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                }}
              />
            ) : (
              <span className={styles.manageTitle} dir="auto">
                {list.title || t('lists.untitled')}
              </span>
            )}
            <div className={styles.manageActions} data-no-drag="true">
              <button
                type="button"
                className={styles.manageIcon}
                aria-label={t('lists.renameList')}
                onClick={() => setEditingId(list.id)}
              >
                <IconPencil />
              </button>
              <button
                type="button"
                className={styles.manageIcon}
                aria-label={t('lists.delete', { name: list.title || t('lists.list') })}
                onClick={() => actions.deleteList(page.id, list.id)}
              >
                <IconMinus />
              </button>
            </div>
          </div>
          );
        })}
      </div>
      <div className={styles.managerFoot}>
        <button type="button" className={styles.done} onClick={onClose}>
          {t('lists.done')}
        </button>
      </div>
      </div>
    </div>,
    document.body
  );
}
